
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface AnimatedCounterProps {
  className?: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const AnimatedCounter = ({ className }: AnimatedCounterProps) => {
  const [count, setCount] = useState(1);
  const [amount, setAmount] = useState(1100);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Start animation after a short delay
    const timeout = setTimeout(() => {
      setIsAnimating(true);
      
      // Set up an interval to increment the counters
      intervalRef.current = setInterval(() => {
        setCount(prevCount => {
          const newCount = prevCount + 1;
          if (newCount > 12) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            return 12;
          }
          return newCount;
        });
        
        setAmount(prevAmount => {
          const increment = Math.floor((14500 - 1100) / 11); // Divide total range by steps
          const newAmount = prevAmount + increment;
          if (newAmount > 14500) {
            return 14500;
          }
          return newAmount;
        });
      }, 2000); // Updated to 2000ms (2 seconds) for each increment
    }, 1000);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className={`${className || ''}`}>
      <AnimatePresence>
        {isAnimating && (
          <motion.div 
            className="bg-black rounded-full inline-flex items-center px-6 py-3 gap-4 border border-white/10"
            initial={{ rotateX: 90, opacity: 0 }}
            animate={{ rotateX: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.span 
              className="text-xl font-bold text-white"
              key={`count-${count}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {count} Trips
            </motion.span>
            
            <motion.span 
              className="text-neon-green text-xl font-bold"
              key={`amount-${amount}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {formatCurrency(amount)}
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
      
      {!isAnimating && (
        <div className="bg-black rounded-full inline-flex items-center px-6 py-3 gap-4 border border-white/10 opacity-0">
          <span className="text-xl font-bold">1 Trip</span>
          <span className="text-neon-green text-xl font-bold">$1,100</span>
        </div>
      )}
    </div>
  );
};

export default AnimatedCounter;
